import { useEffect, useState } from 'react'
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom'
import { Home, Compass, BookOpen, User, RotateCcw, LogOut, ShieldCheck, Moon, Sun, Building2, Send, BrainCircuit } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'

function Layout() {
  const navigate = useNavigate()
  const { usuario, logout } = useAuthStore()
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'))

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark)
    localStorage.setItem('eduplex-theme', isDark ? 'dark' : 'light')
    document.querySelector('meta[name="theme-color"]')?.setAttribute(
      'content',
      isDark ? '#09090b' : '#ffffff'
    )
  }, [isDark])

  const toggleTheme = () => {
    setIsDark(current => !current)
    window.dispatchEvent(new Event('eduplex:theme-change'))
  }

  const podeRecomendar = usuario?.tipo_perfil === 'professor' || usuario?.instituicoes?.some(
    item => ['professor', 'gestor'].includes(item.papel) && item.status === 'ativo'
  )

  const navItems = [
    { to: '/', icon: Home, label: 'Início' },
    { to: '/explorar', icon: Compass, label: 'Explorar' },
    ...(usuario ? [{ to: '/tutor', icon: BrainCircuit, label: 'Tutor IA' }] : []),
    { to: '/caderno', icon: BookOpen, label: 'Caderno', tour: 'caderno' },
    { to: '/revisao', icon: RotateCcw, label: 'Revisar', tour: 'revisao' },
    ...(usuario?.tipo_perfil === 'admin'
      ? [{ to: '/admin', icon: ShieldCheck, label: 'Admin' }]
      : []),
    ...(usuario?.tipo_perfil === 'admin' || usuario?.instituicoes?.length
      ? [{ to: '/instituicao', icon: Building2, label: 'Instituição' }]
      : []),
    ...(podeRecomendar
      ? [{ to: '/recomendar', icon: Send, label: 'Recomendar', desktopOnly: true }]
      : []),
    { to: '/perfil', icon: User, label: 'Perfil' }
  ]

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
      isActive
        ? 'bg-black text-white dark:bg-white dark:text-black'
        : 'text-gray-500 hover:bg-gray-100 hover:text-black dark:text-zinc-400 dark:hover:bg-zinc-900 dark:hover:text-white'
    }`

  return (
    <div className="min-h-screen bg-white text-gray-900 transition-colors dark:bg-zinc-950 dark:text-zinc-100">
      {/* Header */}
      <header className="sticky top-0 z-[110] border-b border-gray-100 bg-white/90 px-4 py-3 backdrop-blur-md transition-colors dark:border-zinc-800 dark:bg-zinc-950/90 sm:px-6">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <Link
            to="/"
            className="flex min-w-0 items-center gap-1.5 min-[380px]:gap-2 sm:gap-2.5"
            aria-label="Agente de Estudo — ir para o início"
          >
            <img
              src="/brand/agente-de-estudo-icone.svg"
              alt=""
              width="36"
              height="36"
              className="h-7 w-7 shrink-0 min-[380px]:h-8 min-[380px]:w-8 sm:h-9 sm:w-9"
            />
            <span className="whitespace-nowrap text-[9px] font-semibold tracking-tight min-[380px]:text-xs sm:text-sm">
              <span className="text-blue-600 dark:text-blue-400">Agente</span>{' '}
              <span className="text-zinc-900 dark:text-zinc-100">de Estudo</span>
            </span>
          </Link>
          <div className="flex items-center gap-1 min-[380px]:gap-1.5">
            <button
              type="button"
              onClick={toggleTheme}
              className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-black dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-white min-[380px]:h-8 min-[380px]:w-8"
              aria-label={isDark ? 'Ativar modo claro' : 'Ativar modo escuro'}
              aria-pressed={isDark}
              title={isDark ? 'Modo claro' : 'Modo escuro'}
            >
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            {usuario ? (
              <button
                type="button"
                onClick={() => {
                  logout()
                  navigate('/login')
                }}
                className="flex items-center gap-2 text-xs text-gray-500 hover:text-black dark:text-zinc-400 dark:hover:text-white"
                title="Sair da conta"
              >
                <span className="flex min-w-0 flex-col items-end gap-0.5">
                  <span className="max-w-24 truncate sm:max-w-48">{usuario.nome}</span>
                  {usuario.tipo_perfil === 'admin' && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-medium leading-none text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                      <ShieldCheck size={10} /> Administrador
                    </span>
                  )}
                </span>
                <LogOut size={14} />
              </button>
            ) : (
              <div className="flex items-center gap-1 sm:gap-2">
                <Link
                  to="/login"
                  className="rounded-lg px-1 py-1.5 text-[10px] font-medium transition-colors hover:bg-gray-100 dark:hover:bg-zinc-800 min-[380px]:px-2 min-[380px]:text-xs sm:px-3"
                >
                  Entrar
                </Link>
                <Link
                  to="/register"
                  className="whitespace-nowrap rounded-lg bg-black px-2 py-1.5 text-[10px] font-medium text-white transition-opacity hover:opacity-80 dark:bg-white dark:text-black min-[380px]:px-2.5 min-[380px]:text-xs sm:px-3"
                >
                  Criar conta
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="mx-auto flex min-h-[calc(100vh-57px)] w-full max-w-7xl">
        {/* Navegação lateral para desktop */}
        <aside className="hidden w-60 flex-shrink-0 border-r border-gray-100 px-4 py-6 dark:border-zinc-800 lg:block">
          <div className="sticky top-24 space-y-1">
            <p className="mb-3 px-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400 dark:text-zinc-600">
              Navegação
            </p>
            {navItems.map(({ to, icon: Icon, label, tour }) => (
              <NavLink key={to} to={to} end={to === '/'} className={navLinkClass} data-tour={tour}>
                <Icon size={18} strokeWidth={2} />
                <span>{label}</span>
              </NavLink>
            ))}
          </div>
        </aside>

        {/* Conteúdo */}
        <main className="min-w-0 flex-1 px-4 py-5 pb-24 sm:px-6 sm:py-7 lg:px-8 lg:py-8 lg:pb-12">
          <Outlet />
        </main>
      </div>

      {/* Navegação inferior para celular e tablet */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-gray-100 bg-white transition-colors dark:border-zinc-800 dark:bg-zinc-950 lg:hidden">
        <div className="max-w-lg mx-auto flex justify-around py-2">
          {navItems.filter(item => !item.desktopOnly).map(({ to, icon: Icon, label, tour }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              data-tour={tour}
              className={({ isActive }) =>
                `flex min-w-0 flex-1 flex-col items-center gap-0.5 px-1 py-1 rounded-lg transition-colors ${
                  isActive ? 'text-black dark:text-white' : 'text-gray-400 dark:text-zinc-500'
                }`
              }
            >
              <Icon size={20} strokeWidth={2} />
              <span className="text-[10px] font-medium">{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  )
}

export default Layout
